import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Spin, message } from 'antd';
import dayjs from 'dayjs';
import api from '../services/api';
import { VoucherForm } from '../components/voucher/VoucherForm';
import {
  getLockedFieldsForEdit,
  buildVoucherUpdatePayload,
  type LockedFieldKey,
} from '../utils/voucherFormHelpers';
import type { VoucherFormValues } from '../components/voucher/VoucherForm';

interface VoucherData {
  id: number;
  code: string;
  name?: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  minimum_order_amount?: number | null;
  max_discount_amount?: number | null;
  valid_from: string;
  valid_until: string;
  total_used?: number;
  is_active?: boolean;
  description?: string;
}

export default function VoucherEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState<VoucherData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadVoucher = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get<{ success?: boolean; data: VoucherData }>(
        `/discount-codes/${id}`
      );
      const data = res.data?.data ?? res.data;
      setVoucher(data || null);
    } catch (err) {
      message.error('Failed to load voucher');
      setVoucher(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadVoucher();
  }, [loadVoucher]);

  const lockedFields: Set<LockedFieldKey> = voucher
    ? getLockedFieldsForEdit(voucher)
    : new Set<LockedFieldKey>();

  const initialValues: VoucherFormValues | undefined = voucher
    ? {
        code: voucher.code,
        campaignName: voucher.name ?? '',
        description: voucher.description ?? '',
        discountType: voucher.discount_type,
        discountValue: voucher.discount_value,
        minimumSpend: voucher.minimum_order_amount ?? 0,
        maxDiscountAmount:
          voucher.max_discount_amount != null
            ? Number(voucher.max_discount_amount)
            : undefined,
        validFrom: voucher.valid_from ? dayjs(voucher.valid_from) : undefined,
        validUntil: voucher.valid_until
          ? dayjs(voucher.valid_until)
          : undefined,
        initialStatus: 'schedule',
      }
    : undefined;

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!id) return;
    const payload = buildVoucherUpdatePayload(values, lockedFields);
    await api.put(`/discount-codes/${id}`, payload);
    message.success('Voucher updated successfully');
    navigate(`/vouchers/${id}`);
  };

  if (loading && !voucher) {
    return (
      <div className="create-voucher-wrap">
        <div className="create-voucher-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="create-voucher-breadcrumb-sep">&gt;</span>
          <Link to="/vouchers">Vouchers</Link>
          <span className="create-voucher-breadcrumb-sep">&gt;</span>
          <span>Edit</span>
        </div>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="create-voucher-wrap">
        <div className="create-voucher-breadcrumb">
          <Link to="/vouchers">Vouchers</Link>
        </div>
        <p>Voucher not found.</p>
      </div>
    );
  }

  return (
    <div className="create-voucher-wrap">
      <div className="create-voucher-breadcrumb">
        <Link to="/">Dashboard</Link>
        <span className="create-voucher-breadcrumb-sep">&gt;</span>
        <Link to="/vouchers">Vouchers</Link>
        <span className="create-voucher-breadcrumb-sep">&gt;</span>
        <Link to={`/vouchers/${id}`}>{voucher.code}</Link>
        <span className="create-voucher-breadcrumb-sep">&gt;</span>
        <span className="create-voucher-breadcrumb-current">Edit</span>
      </div>

      <h1 className="create-voucher-title">Edit Voucher</h1>

      <div className="create-voucher-card">
        <VoucherForm
          mode="edit"
          initialValues={initialValues}
          lockedFields={lockedFields}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/vouchers/${id}`)}
          submitLabel="Save changes"
        />
      </div>
    </div>
  );
}
